import { useState, useEffect } from 'react';
import { KeyRound, Plus, Trash2, Copy, Check, AlertTriangle } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { apiKeysApi } from '../lib/api';
import type { ApiKey, CreatedApiKey } from '../types/teams';

const AVAILABLE_SCOPES = [
  { id: 'vault:read', label: 'vault:read', description: 'Read backups, snapshots, and vault data' },
  { id: 'vault:backup', label: 'vault:backup', description: 'Start backups and manage backup jobs' },
];

const USAGE_SNIPPET = (key: string) =>
  `curl https://api.myleadsyncer.com/api/v1/backups \\\n  -H "Authorization: Bearer ${key}"`;

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(AVAILABLE_SCOPES.map((s) => s.id));
  const [creating, setCreating] = useState(false);

  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const res = await apiKeysApi.list();
      if (res.success && res.data) {
        setKeys(res.data.apiKeys);
      } else {
        setError(res.message || 'Failed to load API keys');
      }
    } catch {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const toggleScope = (scope: string) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed || scopes.length === 0) return;
    setCreating(true);
    try {
      const res = await apiKeysApi.create(trimmed, scopes);
      if (res.success && res.data) {
        setCreatedKey(res.data.apiKey);
        setShowCreateModal(false);
        setName('');
        setScopes(AVAILABLE_SCOPES.map((s) => s.id));
        loadKeys();
      } else {
        setError(res.message || 'Failed to create API key');
      }
    } catch {
      setError('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this API key? Any integrations using it will stop working immediately.')) return;
    try {
      const res = await apiKeysApi.revoke(id);
      if (res.success) {
        setKeys((prev) => prev.filter((k) => k.id !== id));
        if (createdKey?.id === id) setCreatedKey(null);
      } else {
        setError(res.message || 'Failed to revoke API key');
      }
    } catch {
      setError('Failed to revoke API key');
    }
  };

  const copyText = (text: string, setFlag: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setFlag(true);
    setTimeout(() => setFlag(false), 2000);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Developers"
          title="API Keys"
          description="Programmatic access to your vault via the MyLeadSyncer API"
          actions={
            <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
              Create API Key
            </Button>
          }
        />

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-2 text-danger font-bold">&times;</button>
          </div>
        )}

        {/* Newly created key — shown once */}
        {createdKey && (
          <Card padded className="border-accent">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display text-base font-semibold text-ink">
                  Your new API key: {createdKey.name}
                </h3>
                <div className="flex items-start gap-2 text-sm text-warning mt-1.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Copy this key now — you won&apos;t be able to see it again.</span>
                </div>
              </div>
              <Button variant="ghost" onClick={() => setCreatedKey(null)}>
                Done
              </Button>
            </div>

            <div className="relative mt-4">
              <pre className="bg-surface-2 border border-line rounded-[11px] p-4 pr-14 text-[13px] font-mono text-ink overflow-x-auto whitespace-pre-wrap break-all">
                {createdKey.key}
              </pre>
              <button
                onClick={() => copyText(createdKey.key, setCopiedKey)}
                className="absolute top-2 right-2 p-2 bg-surface rounded-[8px] border border-line text-ink-3 hover:text-ink transition-colors"
                title="Copy API key"
              >
                {copiedKey ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            <div className="mt-4">
              <label className="ds-label">Example usage</label>
              <div className="relative">
                <pre className="bg-surface-2 border border-line rounded-[11px] p-4 pr-14 text-[12.5px] font-mono text-ink-2 overflow-x-auto">
                  {USAGE_SNIPPET(createdKey.key)}
                </pre>
                <button
                  onClick={() => copyText(USAGE_SNIPPET(createdKey.key), setCopiedSnippet)}
                  className="absolute top-2 right-2 p-2 bg-surface rounded-[8px] border border-line text-ink-3 hover:text-ink transition-colors"
                  title="Copy snippet"
                >
                  {copiedSnippet ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : keys.length === 0 && !createdKey ? (
          <EmptyState
            icon={KeyRound}
            title="No API keys yet"
            description="Create an API key to access your vault programmatically from scripts and integrations."
            action={
              <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
                Create Your First Key
              </Button>
            }
          />
        ) : keys.length > 0 ? (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface-2">
                    <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Name</th>
                    <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Key</th>
                    <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Scopes</th>
                    <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Last Used</th>
                    <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Created</th>
                    <th className="text-right px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                      <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap font-semibold">{k.name}</td>
                      <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap font-mono">{k.keyPrefix}...</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {k.scopes.map((s) => (
                            <Badge key={s} tone="accent" className="font-mono">{s}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">
                        {new Date(k.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleRevoke(k.id)}
                          className="p-1.5 text-ink-3 hover:text-danger rounded transition-colors"
                          title="Revoke key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}

        {/* Create Key Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">Create API Key</h2>

              <div className="space-y-4">
                <div>
                  <label className="ds-label">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                    placeholder="CI backup script"
                    autoFocus
                    className="ds-input"
                  />
                </div>

                <div>
                  <label className="ds-label">Scopes</label>
                  <div className="space-y-2">
                    {AVAILABLE_SCOPES.map((s) => (
                      <label key={s.id} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={scopes.includes(s.id)}
                          onChange={() => toggleScope(s.id)}
                          className="mt-0.5 rounded border-line text-accent focus:ring-accent-soft"
                        />
                        <span>
                          <span className="text-sm text-ink font-mono">{s.label}</span>
                          <span className="block text-xs text-ink-3">{s.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={creating || !name.trim() || scopes.length === 0}>
                  {creating ? 'Creating...' : 'Create Key'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
